import { addMonths, addYears } from "date-fns"
import { Plan } from "src/types"

interface PlanDetails {
  autoRenewKey: string
  days: number
  durationKey: string
  durationCount: number
  firstPeriodSpecialOfferKey: string
  fullLookupKey: string
  introLookupKey: string
  labelKey: string
  termsCheckboxLabelKey: string
  thisPeriodKey: string
}

const PLANS: readonly Plan[] = ["monthly", "quarterly", "yearly"]

// The plan comes back from the URL, which can be older than the current plans:
// weekly was retired in September 2026, yet payment links with `plan=weekly`
// (restored tabs, history) still open /checkout/payment and crashed it with
// "Unhandled plan: weekly" (Sentry PRODUCTION-VQ). Those links go back to the
// paywall instead.
export function parsePlan(value: string | null | undefined): Plan | undefined {
  return PLANS.find((plan) => plan === value)
}

export function getPlanDetails(plan: Plan): PlanDetails {
  switch (plan) {
    case "monthly":
      return {
        autoRenewKey: "pages.checkout.plans.subscriptionTerms.autoRenewMonth",
        days: 30,
        durationCount: 1,
        durationKey: "pages.checkout.payment.orderSummary.durationMonth",
        firstPeriodSpecialOfferKey:
          "pages.checkout.payment.firstMonthSpecialOffer",
        fullLookupKey: "m-full",
        introLookupKey: "m-intro",
        labelKey: "pages.checkout.plans.oneMonth",
        termsCheckboxLabelKey:
          "pages.checkout.payment.termsCheckboxLabelMonthly",
        thisPeriodKey: "pages.checkout.payment.thisMonth",
      }
    case "quarterly":
      return {
        autoRenewKey: "pages.checkout.plans.subscriptionTerms.autoRenewQuarter",
        days: 90,
        durationCount: 3,
        durationKey: "pages.checkout.payment.orderSummary.durationMonth",
        firstPeriodSpecialOfferKey:
          "pages.checkout.payment.firstQuarterSpecialOffer",
        fullLookupKey: "q-full",
        introLookupKey: "q-intro",
        labelKey: "pages.checkout.plans.threeMonths",
        termsCheckboxLabelKey:
          "pages.checkout.payment.termsCheckboxLabelQuarterly",
        thisPeriodKey: "pages.checkout.payment.thisQuarter",
      }
    case "yearly":
      return {
        autoRenewKey: "pages.checkout.plans.subscriptionTerms.autoRenewYear",
        days: 365,
        durationCount: 1,
        durationKey: "pages.checkout.payment.orderSummary.durationYear",
        firstPeriodSpecialOfferKey:
          "pages.checkout.payment.firstYearSpecialOffer",
        fullLookupKey: "y-full",
        introLookupKey: "y-intro",
        labelKey: "pages.checkout.plans.twelveMonths",
        termsCheckboxLabelKey:
          "pages.checkout.payment.termsCheckboxLabelYearly",
        thisPeriodKey: "pages.checkout.payment.thisYear",
      }
    default: {
      const exhaustiveCheck: never = plan
      throw new Error(`Unhandled plan: ${String(exhaustiveCheck)}`)
    }
  }
}

export function getPlanRenewalDate(plan: Plan, from = new Date()): Date {
  switch (plan) {
    case "monthly":
      return addMonths(from, 1)
    case "quarterly":
      return addMonths(from, 3)
    case "yearly":
      return addYears(from, 1)
    default: {
      const exhaustiveCheck: never = plan
      throw new Error(`Unhandled plan: ${String(exhaustiveCheck)}`)
    }
  }
}
